import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useCallback, type ReactNode } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  content: ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  /** Optional id for the dialog title (accessibility). */
  titleId?: string;
};

/**
 * MUI-based confirm dialog. Cancel is primary (left, default focus); confirm is secondary (right).
 * When the user clicks Confirm, onConfirm is called and then onClose is called, so the dialog
 * always closes and callers do not need to close it in onConfirm.
 */
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  titleId = 'confirm-dialog-title',
}: ConfirmDialogProps) => {
  const handleConfirm = useCallback(() => {
    onConfirm();
    onClose();
  }, [onConfirm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {typeof content === 'string' ? (
          <DialogContentText>{content}</DialogContentText>
        ) : (
          content
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          autoFocus
        >
          {cancelLabel}
        </Button>
        <Button onClick={handleConfirm} color="inherit" variant="outlined">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
